import { useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { JarvisMessage, JarvisPendingAction, JarvisReplyDTO } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { setJarvisHighlight } from "./jarvis-store";

export interface JarvisExchange {
  user: string;
  reply: JarvisReplyDTO | null;
}

const VOICE_OUTPUT_KEY = "af.jarvis.voiceOutput";

/**
 * 10 ms ticha (WAV) — přehráním v rámci uživatelského gesta se audio element
 * „odemkne" pro pozdější programové přehrání (autoplay politika, hlavně Safari:
 * play() po asynchronním fetchi jinak skončí NotAllowedError).
 */
const SILENT_WAV =
  "data:audio/wav;base64,UklGRsQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YaAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA";

/**
 * Sdílená Jarvis konverzace (desktop overlay i mobilní stránka, spec §64–71):
 * stateless server — historii drží hook; navigace + highlight po odpovědi.
 * Hlasový výstup (TTS) je default zapnutý, když je voice nakonfigurovaný;
 * mikrofon (STT) se aktivuje jen na explicitní pokyn (spec §66).
 */
export function useJarvisChat() {
  const navigate = useNavigate();
  const status = useQuery({ queryKey: ["jarvis-status"], queryFn: api.jarvisStatus });
  const [history, setHistory] = useState<JarvisMessage[]>([]);
  const [exchange, setExchange] = useState<JarvisExchange | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [voiceOutput, setVoiceOutputState] = useState(
    () => localStorage.getItem(VOICE_OUTPUT_KEY) !== "0",
  );
  const [recording, setRecording] = useState(false);
  const [speechBlocked, setSpeechBlocked] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const voiceOutputRef = useRef(voiceOutput);

  const voiceAvailable = status.data?.voice ?? false;

  function ensureAudio(): HTMLAudioElement {
    if (!audioRef.current) audioRef.current = new Audio();
    return audioRef.current;
  }

  /** Volat v user gestu (klik/Enter) — bez toho Safari zablokuje pozdější play(). */
  function unlockAudio() {
    if (!voiceOutputRef.current) return;
    const audio = ensureAudio();
    if (!audio.paused) return; // právě mluví — element už je odemčený
    audio.muted = true;
    audio.src = SILENT_WAV;
    void audio.play().catch(() => {});
  }

  const setVoiceOutput = (on: boolean) => {
    voiceOutputRef.current = on;
    setVoiceOutputState(on);
    localStorage.setItem(VOICE_OUTPUT_KEY, on ? "1" : "0");
    if (on) unlockAudio();
    else {
      audioRef.current?.pause();
      setSpeechBlocked(false);
    }
  };

  async function speak(text: string) {
    let url: string;
    try {
      const blob = await api.jarvisTts(text.slice(0, 600));
      url = URL.createObjectURL(blob);
    } catch {
      return; // TTS je best-effort — text už na obrazovce je
    }
    const audio = ensureAudio();
    audio.pause();
    audio.muted = false;
    audio.src = url;
    audio.onended = () => URL.revokeObjectURL(url);
    try {
      await audio.play();
      setSpeechBlocked(false);
    } catch {
      // prohlížeč odmítl autoplay — nabídni ruční přehrání (přímé gesto projde)
      setSpeechBlocked(true);
    }
  }

  /** Ruční přehrání odpovědi, kterou prohlížeč odmítl přehrát sám. */
  const playBlockedSpeech = () => {
    const audio = audioRef.current;
    if (!audio) return;
    setSpeechBlocked(false);
    void audio.play().catch(() => setSpeechBlocked(true));
  };

  const applyReply = (userText: string, reply: JarvisReplyDTO) => {
    setExchange({ user: userText, reply });
    setHistory((prev) => [
      ...prev.slice(-10),
      { role: "user", content: userText },
      { role: "assistant", content: reply.reply },
    ]);
    if (reply.navigate) {
      void navigate({ to: reply.navigate.to });
      setJarvisHighlight(reply.navigate.highlight ?? null);
    }
    if (voiceOutputRef.current && voiceAvailable && reply.reply) void speak(reply.reply);
  };

  const chatMutation = useMutation({
    mutationFn: (message: string) =>
      api.jarvisChat({ message, history, currentView: window.location.pathname }),
    onSuccess: (reply, message) => {
      setError(null);
      applyReply(message, reply);
    },
    onError: (err) => setError(err instanceof Error ? err.message : cs.common.error),
  });
  const confirmMutation = useMutation({
    mutationFn: (action: JarvisPendingAction) =>
      api.jarvisConfirm({ tool: action.tool, args: action.args }),
    onSuccess: (reply) => {
      setExchange((prev) => (prev ? { ...prev, reply } : { user: "", reply }));
      setHistory((prev) => [...prev.slice(-11), { role: "assistant", content: reply.reply }]);
      if (voiceOutputRef.current && voiceAvailable && reply.reply) void speak(reply.reply);
    },
    onError: (err) => setError(err instanceof Error ? err.message : cs.common.error),
  });

  const send = (text: string) => {
    const message = text.trim();
    if (!message || chatMutation.isPending) return;
    unlockAudio(); // send jde vždy z user gesta (klik/Enter)
    setExchange({ user: message, reply: null });
    chatMutation.mutate(message);
  };

  /** Mikrofon se aktivuje jen na výslovný pokyn (spec §66). */
  async function toggleRecording() {
    if (recording) {
      recorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: recorder.mimeType || "audio/webm" });
        try {
          const { text } = await api.jarvisStt(blob);
          if (text.trim()) send(text);
          else setError(cs.jarvis.sttEmpty);
        } catch (err) {
          setError(err instanceof Error ? err.message : cs.common.error);
        }
      };
      recorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch {
      setError(cs.jarvis.micDenied);
    }
  }

  const stop = () => {
    recorderRef.current?.stop();
    audioRef.current?.pause();
  };

  return {
    online: status.data?.online ?? true,
    voiceAvailable,
    model: status.data?.model ?? "",
    exchange,
    error,
    /** Hlasový výstup (TTS) — default zapnutý, přepínatelný v UI. */
    voiceOutput: voiceOutput && voiceAvailable,
    recording,
    pending: chatMutation.isPending,
    confirmPending: confirmMutation.isPending,
    send,
    confirm: (action: JarvisPendingAction) => {
      unlockAudio(); // confirm jde vždy z user gesta (klik)
      confirmMutation.mutate(action);
    },
    setVoiceOutput,
    /** Prohlížeč zablokoval autoplay — UI má nabídnout ruční přehrání. */
    speechBlocked,
    playBlockedSpeech,
    toggleRecording,
    stop,
  };
}
